import { useEffect } from 'react'

import i18n from '../i18n'
import { reminderBody, reminderDueAt, reminderTitle } from '../lib/settings'
import { usePrefsStore } from '../store/prefsStore'
import { useToastStore } from '../store/toastStore'

const FIRED_KEY = 'shadowtooti.reminderFired'
const POLL_MS = 30_000

function todayKey(date) {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
}

/**
 * The daily reminder, as far as a web page can keep it: while Tooti is
 * open in a tab, the clock is read every half minute and, on a ticked day
 * at the set minute, the reminder rings once — a browser notification when
 * the learner has allowed them, and the toast either way. A closed tab
 * cannot ring; the settings page says so under the switch. The Android
 * app's `AlarmManager` reminder is the one that works with the app closed.
 */
export function useReminderClock() {
  const reminder = usePrefsStore((s) => s.reminder)
  const showToast = useToastStore((s) => s.show)

  useEffect(() => {
    if (!reminder.enabled) return undefined
    const check = () => {
      const now = new Date()
      if (!reminderDueAt(reminder, now)) return
      let fired = null
      try {
        fired = localStorage.getItem(FIRED_KEY)
      } catch {
        fired = null
      }
      if (fired === todayKey(now)) return
      try {
        localStorage.setItem(FIRED_KEY, todayKey(now))
      } catch {
        // Without storage the reminder may ring again on the next check; better than never.
      }
      const title = reminderTitle()
      const body = reminderBody()
      showToast(`${title} — ${body}`)
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          new Notification(title, { body, lang: i18n.language, dir: i18n.language === 'en' ? 'ltr' : 'rtl' })
        } catch {
          // Some browsers only allow notifications from a service worker; the toast already showed.
        }
      }
    }
    check()
    const timer = setInterval(check, POLL_MS)
    return () => clearInterval(timer)
  }, [reminder, showToast])
}
